/**
 * More photos on a product card (blocks/birrot-card-photos.liquid).
 *
 * With a mouse from 1025px birrot.css stacks the card's slides and fades the
 * second photo in on hover. The chevrons step on from the photo on show:
 * the gallery gets `data-birrot-stepped` and the slide to show
 * `data-birrot-shown`, which birrot.css fades to, once that slide's photo
 * has loaded (until then the photo before it stays). Leaving the card, by
 * pointer and focus, takes both off, so the card is back on its first photo
 * and the next hover fades to the second again. The photos past the second
 * are not loaded at rest: birrot.css leaves them out of the layout until
 * they are `data-birrot-wanted`, which a hover sets on the ones a step can
 * reach next (either side of the photo on show, wrapping).
 *
 * Below 750px, and at any width without a mouse (a tablet), the gallery is
 * a row the finger swipes (birrot.css, "Mobile"). The
 * line has a segment per slide, built here from the slides themselves (a
 * variant can hide some), and the one for the photo in view carries
 * `data-current` (the line is drawn up to it); that slide is
 * `data-birrot-shown` too, and the photo after it is loaded ahead. From
 * the moment a swipe moves the track the
 * card is `data-birrot-swiped`, on which birrot.css fades its sizes and
 * colours (blocks/birrot-card-variants.liquid) in over the photo's foot;
 * a swipe heading back onto the first photo fades them out, where a
 * swipe comes to rest settles it, and 5s without a swipe fades them out
 * (the next swipe brings them back).
 *
 * Where it swipes the track is also a keyboard stop (tabindex 0, a group
 * named for the product), so the arrow keys scroll the photos a keyboard
 * cannot otherwise reach (axe, scrollable-region-focusable); elsewhere it
 * is stock's tabindex -1 again, as the track does not scroll there.
 *
 * Each change of photo is a `birrot:card-photo` event from the card, on
 * which assets/birrot-card-ink.js reads the text's ink from the photo now
 * on show. Crossing the stack's edge puts the card back on its first
 * photo, step and swipe marks cleared.
 */

const STACKED = window.matchMedia('(min-width: 1025px) and (hover: hover) and (pointer: fine)');

/** Where the track is a swipe (birrot.css, "Mobile"): below 750px, and a tablet at any width. */
const SWIPE = window.matchMedia('(max-width: 749px), not all and (hover: hover) and (pointer: fine)');

const STEPPED = 'data-birrot-stepped';
const SHOWN = 'data-birrot-shown';
const WANTED = 'data-birrot-wanted';

/** The same contract as assets/birrot-product-card.js, which sets it on `load`. */
const LOADED = 'data-birrot-loaded';

/** How long a swipe's track stays still before it counts as settled. */
const SETTLE_WAIT = 80;

/** How long after the last swipe the sizes and colours fade out again. */
const IDLE_WAIT = 5000;

/** How far off the first photo (a fraction of the width) a swipe counts as begun. */
const MOVED = 0.01;

/** How long a step waits for its photo before giving up on it. */
const LOAD_WAIT = 10000;

class BirrotCardPhotos extends HTMLElement {
  /** @type {HTMLElement | null} */
  #card = null;

  /** Bumped by every step and reset: a photo that loads late shows only if nothing came after. */
  #turn = 0;

  /** @type {number | null} */
  #frame = null;

  connectedCallback() {
    this.#card = this.closest('product-card');
    if (!this.#card) return;
    // One photo on show (the rest are a variant's): nothing to step through.
    if (this.#slides.length < 2) {
      this.remove();
      return;
    }
    this.#segments();
    this.addEventListener('click', this.#handleClick);
    this.#card.addEventListener('pointerenter', this.#handleEnter);
    this.#card.addEventListener('pointerleave', this.#handleLeave);
    this.#card.addEventListener('focusout', this.#handleFocusOut);
    // `scroll` does not bubble; a capture on the card hears the gallery's,
    // whichever element a morph left there.
    this.#card.addEventListener('scroll', this.#handleScroll, { capture: true, passive: true });
    STACKED.addEventListener('change', this.#handleBreakpoint);
    SWIPE.addEventListener('change', this.#keyboard);
    this.#keyboard();
  }

  disconnectedCallback() {
    clearTimeout(this.#settle);
    clearTimeout(this.#idle);
    STACKED.removeEventListener('change', this.#handleBreakpoint);
    SWIPE.removeEventListener('change', this.#keyboard);
    if (!this.#card) return;
    this.#card.removeEventListener('pointerenter', this.#handleEnter);
    this.#card.removeEventListener('pointerleave', this.#handleLeave);
    this.#card.removeEventListener('focusout', this.#handleFocusOut);
    this.#card.removeEventListener('scroll', this.#handleScroll, { capture: true });
    this.#card = null;
  }

  /**
   * Across the stack's edge (a tablet turned, a window resized past 1025px)
   * the card is back on its first photo: the stack's track does not scroll,
   * and a swipe's track is laid out again at 0. A step or a swipe made on
   * the other side would otherwise still mark its photo as the one on show.
   */
  #handleBreakpoint = () => {
    this.#turn++;
    clearTimeout(this.#settle);
    clearTimeout(this.#idle);
    const gallery = this.#gallery;
    const slides = this.#slides;
    const line = this.#segments();
    const moved =
      gallery?.hasAttribute(STEPPED) || this.#card?.hasAttribute('data-birrot-swiped') || slides.some((slide) => slide.hasAttribute(SHOWN));
    gallery?.removeAttribute(STEPPED);
    this.#card?.removeAttribute('data-birrot-swiped');
    for (const slide of slides) slide.removeAttribute(SHOWN);
    line.forEach((segment, i) => segment.toggleAttribute('data-current', i === 0));
    this.#position = 0;
    if (moved) this.#notify();
  };

  /** The swipe's track a keyboard stop where it swipes, stock's tabindex -1 elsewhere. */
  #keyboard = () => {
    const scroller = this.#scroller;
    if (!scroller) return;
    if (SWIPE.matches) {
      const name = this.#gallery?.querySelector('[ref="cardGalleryLink"]')?.getAttribute('aria-label');
      scroller.tabIndex = 0;
      scroller.setAttribute('role', 'group');
      if (name) scroller.setAttribute('aria-label', name);
    } else {
      scroller.tabIndex = -1;
      scroller.removeAttribute('role');
      scroller.removeAttribute('aria-label');
    }
  };

  get #gallery() {
    return /** @type {HTMLElement | null} */ (this.#card?.querySelector('.card-gallery') ?? null);
  }

  get #scroller() {
    return /** @type {HTMLElement | null} */ (this.#gallery?.querySelector('slideshow-slides') ?? null);
  }

  /** The slides the gallery shows (a variant's extra photos are `hidden`), in order. */
  get #slides() {
    return /** @type {HTMLElement[]} */ ([...(this.#scroller?.querySelectorAll(':scope > slideshow-slide:not([hidden])') ?? [])]);
  }

  /**
   * The index of the photo on show while hovered: the stepped one, or the
   * second once it has loaded (the hover fade), or the first.
   *
   * @param {HTMLElement[]} slides
   */
  #shownIndex(slides) {
    const shown = slides.findIndex((slide) => slide.hasAttribute(SHOWN));
    if (this.#gallery?.hasAttribute(STEPPED) && shown >= 0) return shown;
    return slides[1]?.querySelector(`img[${LOADED}]`) ? 1 : 0;
  }

  /**
   * Lay out (and so load) the photos either side of `index`.
   *
   * @param {HTMLElement[]} slides
   * @param {number} index
   */
  #want(slides, index) {
    const count = slides.length;
    for (const near of [index - 1, index + 1]) {
      slides[(near + count) % count]?.setAttribute(WANTED, '');
    }
  }

  #notify() {
    this.#card?.dispatchEvent(new CustomEvent('birrot:card-photo', { bubbles: true }));
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const button = /** @type {Element} */ (event.target).closest?.('[data-step]');
    if (!button || !STACKED.matches) return;
    event.preventDefault();
    this.#step(Number(button.getAttribute('data-step')));
  };

  /**
   * The line's segments, one per slide: built here, and again when a morph
   * or a variant has changed the count.
   */
  #segments() {
    const line = this.querySelector('.birrot-card-photos__line');
    const count = this.#slides.length;
    if (line && line.children.length !== count) {
      line.replaceChildren(...Array.from({ length: count }, () => document.createElement('span')));
      line.firstElementChild?.setAttribute('data-current', '');
    }
    return line ? [...line.children] : [];
  }

  /** @param {number} delta */
  async #step(delta) {
    const gallery = this.#gallery;
    const slides = this.#slides;
    if (!gallery || slides.length < 2) return;
    const index = (this.#shownIndex(slides) + delta + slides.length) % slides.length;
    const slide = slides[index];
    const turn = ++this.#turn;
    slide.setAttribute(WANTED, '');
    this.#want(slides, index);

    const image = slide.querySelector('img');
    if (image && !image.hasAttribute(LOADED)) {
      image.loading = 'eager';
      if (!(await waitFor(slide))) return;
    }
    if (turn !== this.#turn) return;
    for (const other of slides) other.toggleAttribute(SHOWN, other === slide);
    gallery.setAttribute(STEPPED, '');
    this.#notify();
  }

  #reset() {
    this.#turn++;
    const gallery = this.#gallery;
    if (!gallery?.hasAttribute(STEPPED)) return;
    gallery.removeAttribute(STEPPED);
    for (const slide of this.#slides) slide.removeAttribute(SHOWN);
    this.#notify();
  }

  // The hover fades to the second photo: the photos either side of it are
  // the ones a step reaches first.
  #handleEnter = () => {
    if (STACKED.matches) this.#want(this.#slides, 1);
  };

  #handleLeave = () => {
    if (this.#card?.matches(':has(:focus-visible)')) return;
    this.#reset();
  };

  /** @param {FocusEvent} event */
  #handleFocusOut = (event) => {
    if (this.#card?.contains(/** @type {Node | null} */ (event.relatedTarget)) || this.#card?.matches(':hover')) return;
    this.#reset();
  };

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #settle;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #idle;

  /** The track's last position, in photos: which way a swipe heads. */
  #position = 0;

  /** @param {Event} event */
  #handleScroll = (event) => {
    const scroller = /** @type {HTMLElement} */ (event.target);
    if (scroller.localName !== 'slideshow-slides') return;
    // Settled: no scroll for this long (`scrollend` is not in every Safari).
    clearTimeout(this.#settle);
    this.#settle = setTimeout(() => this.#settled(scroller), SETTLE_WAIT);
    // Left alone this long after the last swipe, the sizes and colours go.
    clearTimeout(this.#idle);
    this.#idle = setTimeout(() => this.#card?.removeAttribute('data-birrot-swiped'), IDLE_WAIT);
    if (this.#frame !== null) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#follow(scroller);
    });
  };

  /**
   * The track's position, in photos (RTL scrolls negative).
   *
   * @param {HTMLElement} scroller
   */
  #positionOf(scroller) {
    return scroller.clientWidth ? Math.abs(scroller.scrollLeft) / scroller.clientWidth : 0;
  }

  /**
   * The line follows the swipe, and the photo after the one in view loads
   * ahead.
   *
   * @param {HTMLElement} scroller
   */
  #follow(scroller) {
    const position = this.#positionOf(scroller);
    // The sizes and colours fade in with any swipe off the first photo
    // (again after IDLE_WAIT took them away), and out as soon as one heads
    // back onto it.
    const heading = position - this.#position;
    this.#position = position;
    if (heading < 0 && position < 1 - MOVED) this.#card?.removeAttribute('data-birrot-swiped');
    else if (position > MOVED) this.#card?.setAttribute('data-birrot-swiped', '');

    const segments = this.#segments();
    const index = Math.min(segments.length - 1, Math.round(position));
    if (segments[index]?.hasAttribute('data-current')) return;
    segments.forEach((segment, i) => segment.toggleAttribute('data-current', i === index));
    const slides = this.#slides;
    slides.forEach((slide, i) => slide.toggleAttribute(SHOWN, i === index));
    const next = slides[index + 1]?.querySelector('img');
    if (next && next.loading === 'lazy') next.loading = 'eager';
    this.#notify();
  }

  /**
   * The sizes and colours (birrot.css, "Mobile") show as soon as a swipe
   * heads off the first photo and go as soon as one heads back onto it
   * (#follow); here, once it has come to rest, they are put right for
   * where it stopped (a swipe let go that snapped back).
   *
   * @param {HTMLElement} scroller
   */
  #settled(scroller) {
    this.#card?.toggleAttribute('data-birrot-swiped', Math.round(this.#positionOf(scroller)) > 0);
  }
}

/**
 * Resolves true once the slide's photo loads, false if it takes too long.
 * A `load` captured on the slide, not the image: a failed photo is put back
 * as a new element (assets/birrot-product-card.js), as
 * assets/birrot-card-ink.js `again()` listens.
 *
 * @param {Element} slide
 * @returns {Promise<boolean>}
 */
function waitFor(slide) {
  return new Promise((resolve) => {
    const done = (/** @type {boolean} */ loaded) => {
      clearTimeout(timer);
      slide.removeEventListener('load', onLoad, true);
      resolve(loaded);
    };
    const onLoad = () => done(true);
    const timer = setTimeout(() => done(false), LOAD_WAIT);
    slide.addEventListener('load', onLoad, true);
  });
}

if (!customElements.get('birrot-card-photos')) customElements.define('birrot-card-photos', BirrotCardPhotos);
